import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { listsQuery, useCreateList, useRenameList, useDeleteList } from '../lib/contacts.js';

/**
 * Listen verwalten.
 *
 * Bis hierher liess sich eine Liste nur beim Import auswaehlen — anlegen,
 * umbenennen oder entfernen ging in der Oberflaeche gar nicht, obwohl die
 * Endpunkte dafuer seit Anfang an bestehen. Ein Tippfehler im Namen war damit
 * dauerhaft.
 *
 * Das Loeschen entfernt die Liste, nicht die Kontakte darin. Das steht auch so
 * in der Rueckfrage: bei einer Liste mit 4000 Adressen ist das der Unterschied,
 * den man vorher wissen will, nicht hinterher.
 */
export function ListManagerDialog({ onClose }: { onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const lists = useQuery(listsQuery);
  const create = useCreateList();
  const rename = useRenameList();
  const remove = useDeleteList();

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  const fehler = [create.error, rename.error, remove.error].find(Boolean);

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="list-manager-title"
        ref={dialogRef}
      >
        <div className="card__body stack">
          <h2 id="list-manager-title" style={{ fontSize: 'var(--text-lg)' }}>
            Listen verwalten
          </h2>

          {fehler && (
            <div className="alert" data-tone="danger" role="alert">
              {fehler instanceof Error ? fehler.message : 'Die Aktion ist fehlgeschlagen.'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input
              className="input"
              style={{ flex: 1 }}
              value={newName}
              maxLength={120}
              placeholder="Name der neuen Liste"
              aria-label="Name der neuen Liste"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void anlegen();
                }
              }}
            />
            <Button
              onClick={() => void anlegen()}
              disabled={newName.trim().length === 0}
              loading={create.isPending}
            >
              Anlegen
            </Button>
          </div>

          {lists.data?.items.length === 0 && (
            <p className="muted text-sm">Noch keine Liste. Die erste legst du oben an.</p>
          )}

          {(lists.data?.items.length ?? 0) > 0 && (
            <div className="table-wrap" style={{ maxHeight: '340px', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Liste</th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Kontakte
                    </th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {lists.data?.items.map((liste) => (
                    <tr key={liste.id}>
                      <td>
                        {editingId === liste.id ? (
                          <input
                            className="input"
                            value={editingName}
                            maxLength={120}
                            aria-label={`Neuer Name für ${liste.name}`}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void umbenennen(liste.id);
                              }
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                        ) : (
                          liste.name
                        )}
                      </td>
                      <td data-numeric style={{ textAlign: 'right' }}>
                        {liste.contactCount.toLocaleString('de-CH')}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {editingId === liste.id ? (
                          <>
                            <Button
                              onClick={() => void umbenennen(liste.id)}
                              loading={rename.isPending}
                            >
                              Speichern
                            </Button>{' '}
                            <Button variant="secondary" onClick={() => setEditingId(null)}>
                              Abbrechen
                            </Button>
                          </>
                        ) : confirmId === liste.id ? (
                          <>
                            <span className="text-sm" style={{ marginRight: 'var(--space-2)' }}>
                              Liste löschen? Die Kontakte darin bleiben bestehen.
                            </span>
                            <Button variant="danger" onClick={() => void loeschen(liste.id)}>
                              Löschen
                            </Button>{' '}
                            <Button variant="secondary" onClick={() => setConfirmId(null)}>
                              Abbrechen
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setEditingId(liste.id);
                                setEditingName(liste.name);
                                setConfirmId(null);
                              }}
                            >
                              Umbenennen
                            </Button>{' '}
                            <Button variant="ghost" onClick={() => setConfirmId(liste.id)}>
                              Löschen
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Schliessen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  async function anlegen() {
    const name = newName.trim();
    if (name.length === 0) return;
    await create.mutateAsync({ name });
    setNewName('');
  }

  async function umbenennen(id: string) {
    const name = editingName.trim();
    if (name.length === 0) return;
    await rename.mutateAsync({ id, name });
    setEditingId(null);
  }

  async function loeschen(id: string) {
    await remove.mutateAsync(id);
    setConfirmId(null);
  }
}
